import { useState } from 'react';
import {
  Button,
  DataTable,
  ErrorState,
  FilterBar,
  Modal,
  Pager,
  ReasonField,
  SelectableChip,
  StatusPill,
  isReasonValid,
  type DataTableColumn,
} from '../../components';
import { useSession } from '../../lib/auth/useSession';
import { formatDate } from '../../lib/format/dates';
import { useTableUrlState } from '../../lib/table/tableSearch';
import { notify } from '../../services/notify';
import { pickParam } from '../profiles/people';
import type { RegistrationRow } from './leversApi';
import { useMarkLoadedInStripe, useRegistrations } from './leversQueries';
import { STATUS_TONE, humanStatus } from './parts';

const STATUSES = ['active', 'pending', 'closed'] as const;
const TYPES = ['state', 'county', 'special_district', 'city'] as const;

const WHO_FILES: Record<string, string> = { stripe: 'Stripe', kurtz_fargo: 'Kurtz Fargo', internal: 'Internal' };

function columns(
  lock: string | undefined,
  onMarkLoaded: (r: RegistrationRow) => void,
): DataTableColumn<RegistrationRow>[] {
  return [
    {
      id: 'jurisdiction',
      header: 'Jurisdiction',
      width: '1.6fr',
      cell: (r) => (
        <span className="min-w-0">
          <span className="block truncate font-semibold">{r.jurisdictionName}</span>
          <span className="block text-xs text-muted">{humanStatus(r.registration.jurisdictionType)}</span>
        </span>
      ),
    },
    { id: 'type', header: 'Type', width: '1fr', cell: (r) => humanStatus(r.registration.type) },
    {
      id: 'license',
      header: 'License',
      width: '1.2fr',
      cell: (r) => <span className="truncate font-mono text-[12.5px]">{r.registration.licenseNumber || '—'}</span>,
    },
    {
      id: 'effective',
      header: 'Effective',
      width: '112px',
      cell: (r) => formatDate(r.registration.effectiveDate, 'UTC'),
    },
    {
      id: 'filing',
      header: 'Filing',
      width: '1fr',
      cell: (r) => (
        <span className="text-[13px]">
          {humanStatus(r.registration.filingFrequency || '—')}
          <span className="block text-xs text-muted">
            {WHO_FILES[r.registration.whoFiles] ?? (r.registration.whoFiles || '—')}
            {r.registration.nextDueDate ? ` · next ${formatDate(r.registration.nextDueDate, 'UTC')}` : ''}
          </span>
        </span>
      ),
    },
    {
      id: 'stripe',
      header: 'In Stripe',
      width: '150px',
      cell: (r) =>
        r.registration.loadedIntoStripe ? (
          <StatusPill tone="success" dot>
            Loaded
          </StatusPill>
        ) : (
          <Button
            variant="ghost-dark"
            size="sm"
            lockedReason={lock}
            onClick={(e) => {
              e.stopPropagation();
              onMarkLoaded(r);
            }}
          >
            Mark loaded
          </Button>
        ),
    },
    {
      id: 'status',
      header: 'Status',
      width: '112px',
      cell: (r) => (
        <StatusPill tone={STATUS_TONE[r.registration.status] ?? 'neutral'} dot>
          {humanStatus(r.registration.status)}
        </StatusPill>
      ),
    },
  ];
}

/**
 * Tax registrations (B5): every license held, by jurisdiction. A license only
 * counts once it is loaded into Stripe; marking it loaded is audited. City
 * registrations are created from Colorado home rule ("Register").
 */
export function RegistrationsScreen() {
  const table = useTableUrlState();
  const { locked } = useSession();
  const status = pickParam(table.filters.status, STATUSES);
  const type = pickParam(table.filters.type, TYPES);
  const q = useRegistrations({ page: table.page, pageSize: table.pageSize, status, jurisdictionType: type });
  const [marking, setMarking] = useState<RegistrationRow | null>(null);

  return (
    <div>
      <FilterBar>
        <SelectableChip active={!status} onClick={() => table.setFilter('status', undefined)}>
          All
        </SelectableChip>
        {STATUSES.map((s) => (
          <SelectableChip key={s} active={status === s} onClick={() => table.setFilter('status', s)}>
            {humanStatus(s)}
          </SelectableChip>
        ))}
        <span className="mx-1 h-6 w-px bg-[rgba(143,68,32,.14)]" aria-hidden="true" />
        {TYPES.map((t) => (
          <SelectableChip
            key={t}
            active={type === t}
            onClick={() => table.setFilter('type', type === t ? undefined : t)}
          >
            {humanStatus(t)}
          </SelectableChip>
        ))}
      </FilterBar>
      <DataTable
        label="Registrations"
        minWidth={1100}
        columns={columns(locked('registrations.write'), setMarking)}
        rows={q.data?.items ?? []}
        getRowId={(r) => r.registration.id}
        loading={q.isPending}
        error={q.error}
        onRetry={() => void q.refetch()}
        emptyLabel="No registrations match those filters."
      />
      <Pager
        page={table.page}
        pageSize={table.pageSize}
        total={q.data?.total ?? 0}
        onPageChange={table.setPage}
        onPageSizeChange={table.setPageSize}
        noun="registrations"
      />
      {marking && <MarkLoadedDialog row={marking} onClose={() => setMarking(null)} />}
    </div>
  );
}

function MarkLoadedDialog({ row, onClose }: { row: RegistrationRow; onClose: () => void }) {
  const mark = useMarkLoadedInStripe();
  const [reason, setReason] = useState('');
  return (
    <Modal
      isOpen
      onClose={onClose}
      size="md"
      title={`Mark ${row.jurisdictionName} loaded into Stripe?`}
      footer={
        <>
          <Button variant="ghost-dark" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={!isReasonValid(reason)}
            loading={mark.isPending}
            onClick={() =>
              mark.mutate(
                { id: row.registration.id, reason: reason.trim() },
                {
                  onSuccess: () => {
                    notify.success('Marked loaded into Stripe.');
                    onClose();
                  },
                },
              )
            }
          >
            Mark loaded
          </Button>
        </>
      }
    >
      <p className="mt-0 text-sm text-muted">
        License <span className="font-mono">{row.registration.licenseNumber || '—'}</span>. Stripe collects on it from
        now.
      </p>
      <ReasonField value={reason} onChange={setReason} />
      {mark.error && <ErrorState error={mark.error} compact />}
    </Modal>
  );
}
